import React, { useEffect, useState } from 'react';
import { Spinner } from '../UI';
import * as api from '../../api';
import { fmtDate } from './mpoUtils';

// Approval Progress & History for a purchase order — the same rail Enterprise Requisition and Material Request
// draw from the workflow engine's tracking: submitter, every step that acted (approved / returned / rejected),
// the step it now waits on (or the requester, when it was returned to them), and the next step.

const fmtDateTime = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString('en-GB'); } catch { return v; }
};

const HIST_STYLE = {
  approved: { color: '#10b981', icon: <polyline points="20 6 9 17 4 12" /> },
  returned: { color: '#f97316', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
  rejected: { color: '#ef4444', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
};

const Connector = ({ color, dashed }) => (
  <div style={{ flex: '1 1 60px', minWidth: 60, height: 2, marginTop: 19, ...(dashed ? { borderTop: `2px dashed ${color}` } : { background: color }) }} />
);

function Node({ color, dashed, faded, children, title, sub, when, note }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 140, maxWidth: 190, opacity: faded ? 0.65 : 1 }}>
      <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px ${dashed ? 'dashed' : 'solid'} ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px ${dashed ? 'dashed' : 'solid'} ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
          {children}
        </div>
      </div>
      <div style={{ fontWeight: 600, fontSize: 12, marginTop: 6, color: faded ? '#64748b' : undefined }}>{title}</div>
      {sub && <div style={{ color: '#475569', fontSize: 11 }}>{sub}</div>}
      {when && <div style={{ color: '#94a3b8', fontSize: 10 }}>{when}</div>}
      {note && <div style={{ marginTop: 3, fontStyle: 'italic', color: '#475569', fontSize: 10 }}>"{note}"</div>}
    </div>
  );
}

export default function MPOApprovalHistory({ po }) {
  const instanceId = po?.workflow_instance_id;
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!instanceId) { setTracking(null); return undefined; }
    let alive = true;
    setLoading(true);
    api.getWorkflowTracking(instanceId)
      .then((res) => { if (alive) setTracking(res?.data || res || null); })
      .catch(() => { if (alive) setTracking(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // the status changes on every workflow action, so a refreshed PO reloads its history
  }, [instanceId, po?.status, po?.updated_at]);

  const history = tracking?.approval_history || [];
  const current = tracking?.current_step || null;
  // returned all the way to the requester: no step is pending, the PO waits on them to correct and resubmit
  const withRequester = po?.status === 'returned' && !current;

  const nodes = [
    ...history.map((h, i) => ({ kind: 'history', key: `h-${h.step_id || i}`, order: h.step_order ?? i + 1, h })),
    ...(current ? [{ kind: 'current', key: 'cur', order: current.step_order ?? Number.MAX_SAFE_INTEGER }] : []),
  ].sort((a, b) => a.order - b.order);

  return (
    <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '14px 18px' }}>
      <style>{`
        @keyframes mpo-hg-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .mpo-hg-pending { animation: mpo-hg-spin 2s linear infinite; transform-origin: center; }
      `}</style>
      <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
        </svg>
        Approval Progress &amp; History
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 13 }}><Spinner size={14} /> Loading approval history…</div>
      ) : !tracking ? (
        <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
          No approval history yet — this purchase order has not been submitted.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
          <Node
            color="#10b981"
            title="Submitted By"
            sub={tracking.requester_details?.full_name || po.submitted_by_name || po.created_by_name || '—'}
            when={po.submitted_date ? fmtDate(po.submitted_date) : null}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
          </Node>

          {nodes.map((node) => {
            if (node.kind === 'history') {
              const { h } = node;
              const hs = HIST_STYLE[h.status] || HIST_STYLE.approved;
              const outcome = h.status === 'returned' ? ' (Returned)' : h.status === 'rejected' ? ' (Rejected)' : '';
              return (
                <React.Fragment key={node.key}>
                  <Connector color={hs.color} />
                  <Node color={hs.color} title={`${h.step_name}${outcome}`} sub={h.approver_fullname} when={fmtDateTime(h.date_actioned)} note={h.comments}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">{hs.icon}</svg>
                  </Node>
                </React.Fragment>
              );
            }
            const onHold = current.is_on_hold;
            const color = onHold ? '#f97316' : '#eab308';
            return (
              <React.Fragment key={node.key}>
                <Connector color="#10b981" />
                <Node
                  color={color}
                  title={<span style={{ color }}>{current.step_name}<div style={{ fontSize: 11 }}>{onHold ? '(On Hold)' : '(Pending)'}</div></span>}
                  sub={current.assigned_to?.full_name}
                  note={onHold ? current.hold_comment : null}
                >
                  {onHold ? (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <svg className="mpo-hg-pending" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M5 22h14" /><path d="M5 2h14" />
                      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                    </svg>
                  )}
                </Node>
              </React.Fragment>
            );
          })}

          {withRequester && (
            <>
              <Connector color="#f97316" />
              <Node
                color="#f97316"
                title={<span style={{ color: '#f97316' }}>With Requester<div style={{ fontSize: 11 }}>(Awaiting correction)</div></span>}
                sub={tracking.requester_details?.full_name || po.created_by_name}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Node>
            </>
          )}

          {tracking.next_step && (
            <>
              <Connector color="#94a3b8" dashed />
              <Node color="#94a3b8" dashed faded title={`${tracking.next_step.step_name} (Next)`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                </svg>
              </Node>
            </>
          )}
        </div>
      )}
    </div>
  );
}
